import { useState, useEffect } from "react";
import useLearningPlatform from "../../../api/learningPlatform";
import {
  Loader2,
  Check,
  Plus,
  Trash2,
  Tag,
  Percent,
  Euro,
  Power,
} from "lucide-react";
import {
  FormField,
  inputClass,
  buttonClass,
  SectionLoader,
  isNotFoundError,
  EmptyState,
} from "./shared";

const DEFAULT_SETTINGS = {
  discount_codes: [],
  allow_free_preview: true,
  bundle_text: "",
};

const Toggle = ({ checked, onChange, isDark, label, description }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="flex-1 min-w-0">
      <div className={`text-[12px] font-medium ${isDark ? "text-gray-200" : "text-gray-800"}`}>
        {label}
      </div>
      {description && (
        <div className={`text-[11px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          {description}
        </div>
      )}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative h-5 w-9 rounded-full transition-colors flex-shrink-0 ${
        checked ? "bg-[#1E4FCF]" : isDark ? "bg-white/[0.08]" : "bg-gray-200"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
          checked ? "translate-x-4" : ""
        }`}
      />
    </button>
  </div>
);

const normaliseCode = (code) => ({
  code: (code.code || "").trim().toUpperCase(),
  type: code.type === "fixed" ? "fixed" : "percentage",
  value: Number(code.value) || 0,
  active: code.active !== false,
});

const DiscountForm = ({ onAdd, onCancel, isDark }) => {
  const [draft, setDraft] = useState({
    code: "",
    type: "percentage",
    value: "",
    active: true,
  });
  const [err, setErr] = useState(null);

  const submit = () => {
    const code = (draft.code || "").trim().toUpperCase();
    const value = Number(draft.value);
    if (!code) return setErr("Code is verplicht");
    if (!/^[A-Z0-9_-]+$/.test(code))
      return setErr("Alleen letters, cijfers, _ en - toegestaan");
    if (!value || value <= 0) return setErr("Waarde moet groter dan 0 zijn");
    if (draft.type === "percentage" && value > 100)
      return setErr("Percentage maximum 100");
    onAdd(normaliseCode({ ...draft, code, value }));
  };

  return (
    <div
      className={`rounded-md border p-3 space-y-3 ${
        isDark
          ? "bg-white/[0.02] border-white/[0.08]"
          : "bg-gray-50 border-gray-200"
      }`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-2">
        <input
          type="text"
          value={draft.code}
          onChange={(e) =>
            setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))
          }
          placeholder="WELKOM10"
          className={inputClass(isDark)}
        />
        <select
          value={draft.type}
          onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value }))}
          className={inputClass(isDark)}
        >
          <option value="percentage">Percentage (%)</option>
          <option value="fixed">Vast bedrag (€)</option>
        </select>
        <input
          type="number"
          min="0"
          step="0.01"
          value={draft.value}
          onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
          placeholder="10"
          className={inputClass(isDark)}
        />
      </div>
      {err && (
        <div
          className={`text-[11px] px-2 py-1 rounded ${
            isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
          }`}
        >
          {err}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className={buttonClass(isDark, "ghost")}
        >
          Annuleer
        </button>
        <button
          type="button"
          onClick={submit}
          className={buttonClass(isDark, "primary")}
        >
          Toevoegen
        </button>
      </div>
    </div>
  );
};

const PricingTab = ({ isDark }) => {
  const { getSettings, updateSettings } = useLearningPlatform();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [original, setOriginal] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSettings();
        if (cancelled) return;
        const codes = Array.isArray(data?.discount_codes)
          ? data.discount_codes.map(normaliseCode)
          : [];
        const merged = {
          ...DEFAULT_SETTINGS,
          ...(data || {}),
          discount_codes: codes,
        };
        setSettings(merged);
        setOriginal(merged);
      } catch (e) {
        if (!isNotFoundError(e)) setError("Kon instellingen niet laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getSettings]);

  const dirty = JSON.stringify(settings) !== JSON.stringify(original);

  const update = (patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
    setSavedAt(null);
  };

  const addCode = (code) => {
    if (settings.discount_codes.find((c) => c.code === code.code)) {
      setError("Deze code bestaat al");
      return;
    }
    setError(null);
    update({ discount_codes: [...settings.discount_codes, code] });
    setAdding(false);
  };

  const removeCode = (codeStr) => {
    update({
      discount_codes: settings.discount_codes.filter((c) => c.code !== codeStr),
    });
  };

  const toggleCode = (codeStr) => {
    update({
      discount_codes: settings.discount_codes.map((c) =>
        c.code === codeStr ? { ...c, active: !c.active } : c
      ),
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...settings,
        discount_codes: settings.discount_codes.map(normaliseCode),
      };
      const data = await updateSettings(payload);
      const codes = Array.isArray(data?.discount_codes)
        ? data.discount_codes.map(normaliseCode)
        : payload.discount_codes;
      const merged = {
        ...DEFAULT_SETTINGS,
        ...(data || settings),
        discount_codes: codes,
      };
      setSettings(merged);
      setOriginal(merged);
      setSavedAt(Date.now());
    } catch (e) {
      setError("Kon niet opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSettings(original);
    setError(null);
    setSavedAt(null);
    setAdding(false);
  };

  if (loading) return <SectionLoader isDark={isDark} />;

  return (
    <div className="space-y-8 max-w-3xl">
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3
              className={`text-[14px] font-semibold flex items-center gap-2 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              <Tag size={14} /> Kortingscodes
            </h3>
            <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              Genereer codes die studenten bij checkout kunnen invoeren
            </p>
          </div>
          {!adding && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className={buttonClass(isDark, "primary")}
            >
              <Plus size={13} /> Voeg code toe
            </button>
          )}
        </div>

        {adding && (
          <DiscountForm
            isDark={isDark}
            onAdd={addCode}
            onCancel={() => setAdding(false)}
          />
        )}

        {settings.discount_codes.length === 0 && !adding ? (
          <EmptyState
            isDark={isDark}
            title="Nog geen kortingscodes"
            description="Voeg een code toe om kortingen aan studenten te geven."
          />
        ) : (
          <div className="space-y-1.5">
            {settings.discount_codes.map((code) => (
              <div
                key={code.code}
                className={`flex items-center gap-3 px-3 py-2 rounded-md border ${
                  isDark
                    ? "bg-white/[0.02] border-white/[0.06]"
                    : "bg-white border-gray-200"
                } ${!code.active ? "opacity-50" : ""}`}
              >
                <div
                  className={`h-7 w-7 rounded flex items-center justify-center ${
                    isDark ? "bg-white/[0.06] text-[#4d7ee0]" : "bg-[#1E4FCF]/10 text-[#1E4FCF]"
                  }`}
                >
                  {code.type === "percentage" ? (
                    <Percent size={12} />
                  ) : (
                    <Euro size={12} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-[12px] font-mono font-semibold ${
                      isDark ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    {code.code}
                  </div>
                  <div className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    {code.type === "percentage"
                      ? `${code.value}% korting`
                      : `€${code.value.toFixed(2)} korting`}
                    {!code.active && " · inactief"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggleCode(code.code)}
                  title={code.active ? "Deactiveer" : "Activeer"}
                  className={`p-1.5 rounded transition-colors ${
                    isDark
                      ? "text-gray-500 hover:text-gray-200 hover:bg-white/[0.06]"
                      : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Power size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => removeCode(code.code)}
                  title="Verwijder"
                  className={`p-1.5 rounded transition-colors ${
                    isDark
                      ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10"
                      : "text-gray-400 hover:text-red-600 hover:bg-red-50"
                  }`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h3
            className={`text-[14px] font-semibold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Gratis preview lessen
          </h3>
          <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            Geef niet-ingeschreven bezoekers een voorproefje
          </p>
        </div>
        <div
          className={`rounded-lg border p-4 ${
            isDark
              ? "bg-white/[0.02] border-white/[0.06]"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <Toggle
            checked={!!settings.allow_free_preview}
            onChange={(v) => update({ allow_free_preview: v })}
            isDark={isDark}
            label="Sta preview lessen toe"
            description="Lessen gemarkeerd als 'preview' zijn vrij toegankelijk voor iedereen"
          />
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h3
            className={`text-[14px] font-semibold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Bundel-aanbod
          </h3>
          <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            Vrije tekst die getoond wordt bij meerdere cursussen
          </p>
        </div>
        <FormField label="Bundel tekst" isDark={isDark}>
          <textarea
            rows={3}
            value={settings.bundle_text}
            onChange={(e) => update({ bundle_text: e.target.value })}
            placeholder="bv. Koop 3 cursussen en krijg 20% korting"
            className={inputClass(isDark)}
          />
        </FormField>
      </section>

      {error && (
        <div
          className={`text-[12px] px-3 py-2 rounded-md ${
            isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
          }`}
        >
          {error}
        </div>
      )}

      <div
        className={`flex items-center justify-end gap-2 pt-2 border-t ${
          isDark ? "border-white/[0.06]" : "border-gray-100"
        }`}
      >
        {savedAt && !dirty && (
          <span
            className={`flex items-center gap-1 text-[12px] mr-auto ${
              isDark ? "text-emerald-400" : "text-emerald-600"
            }`}
          >
            <Check size={13} /> Opgeslagen
          </span>
        )}
        <button
          type="button"
          onClick={handleCancel}
          disabled={!dirty || saving}
          className={buttonClass(isDark, "ghost")}
        >
          Annuleer
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving}
          className={buttonClass(isDark, "primary")}
        >
          {saving && <Loader2 size={13} className="animate-spin" />}
          Opslaan
        </button>
      </div>
    </div>
  );
};

export default PricingTab;
